import React, { useState, useContext, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { ReactComponent as Plus } from '../../assets/svg/plus.svg';
import { ReactComponent as Edit } from '../../assets/svg/edit.svg'; 
import { Switch } from "../Shared/Buttons/Switch";
import { 
    postStageEmailTemplate, 
    updateStageEmailTemplate, 
    deleteStageEmailTemplate, 
    getStageEmailInitials, 
    submitVacancy } from "../../services/VacancyService";
import useErrorPage from "../../hooks/useErrorPage";
import { JobApplicationStageEmail, StageEmailModel } from "../../models/JobApplicationStageEmail";
import { Selector } from "../Shared/Selector";
import EmailHtmlEditor from "../Shared/Inputs/EmailHtmlEditor";
import { Button } from "../Shared/Buttons/Button";
import { TextInput } from "../Shared/Inputs/TextInput";
import { CandidateToEmailTemplate } from "../../helpers/LayoutTexts";
import { ResponseModel } from "../../models/ResponseModel";
import { useAlert } from "../../hooks/useAlert";
import { PopUpWindow } from '../Shared/PopUpWindow';
import { ReplyMailLoader } from "../../components/Loaders/ReplyMailLoader";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedRecruiter } from "../../models/LoggedRecruiter";
import { SelectorModel } from "../../models/SelectorModel";
import { SummaryOfCompletionModel } from "../../models/SummaryOfCompletionModel";
import { EmailSubjectValidation } from "../../models/VacancyPostingModel";
import { TabComponentRef } from "../../models/TabComponentRef";
import { useNavigate } from "react-router-dom";

interface Props {  
    styles: any;
    jobId?: number;
    onUpdateSummary: (model?: Partial<SummaryOfCompletionModel>) => void;
    onTabChange: (tab: string) => void;
    isCVLess: boolean;
    isFinalStage?: boolean;
} 

const initialValidation: EmailSubjectValidation = {
    subject: {}
}

export const ReplyMail = forwardRef<TabComponentRef, Props>((props, ref) => {
    const jobId = props.jobId;
    const styles = props.styles;
    const isFinalStage = props.isFinalStage;
    const navigate = useNavigate();
    const authContext = useContext(AuthContext);
    const recruiter: LoggedRecruiter = authContext.getLoggedRecruiter();    
    const [check, setCheck] = useState<boolean>(false);    
    const [applicationStatuses, setApplicationStatuses] = useState<SelectorModel[]>([]);
    const [stageEmails, setStateEmails] = useState<JobApplicationStageEmail[]>([]);    
    const [editEnable, setEditEnable] = useState<boolean>(false);
    const [editEmailIndex, setEditEmailIndex] = useState<number>(0);
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);    
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [validation, setValidation] = useState<EmailSubjectValidation>(initialValidation);
    const [toErrorPage] = useErrorPage();
    const { Alert, openAlert } = useAlert();
    const topRef = useRef<HTMLDivElement>(null);
    const lastMailRef = useRef<HTMLDivElement | null>(null);

    const initailEmailModel: JobApplicationStageEmail = {
        jobApplicationStageEmailId: 0,
        jobId: jobId,
        applicationStatusId: 10,        
        triggerTime: 0,
        subject: "",        
        template: CandidateToEmailTemplate
    };
    const [emailModel, setEmailModel] = useState<JobApplicationStageEmail>(initailEmailModel);

    useEffect(()=> {
        getStageEmailInitialModel();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [])
    const triggerTimes: SelectorModel[] = [
        { id: 0, value: "Instant" },
        { id: 1, value: "1 Hour" },
        { id: 24, value: "24 Hours" },
        { id: 48, value: "2 Days" },
        { id: 120, value: "5 Days" },
        { id: 168, value: "7 Days" },
        { id: 240, value: "10 Days" }
    ];

    async function getStageEmailInitialModel() {
        setIsLoading(true); 
        await getStageEmailInitials(jobId)
        .then((model: StageEmailModel) => {                    
            setApplicationStatuses(model.applicationStatuses);
            setStateEmails(model.emailTemplates);
            setIsLoading(false);
        })
        .catch((error) => {
            toErrorPage(error);
        });
    }
    
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setEmailModel((values: any) => ({ ...values, [name]: value }));

        if(validation[name] && validation[name]!.invalid) {
            validateInput(name, value);
        }
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
                
        validateInput(name, value);        
    }

    const handleSelectItems = (selector: string, selectedItem: SelectorModel) => {
        if(selector === "applicationStatus") {
            setEmailModel((values: any) => ({ ...values, applicationStatusId: selectedItem.id }));       
        }
        if(selector === "triggerTime") {            
            setEmailModel((values: any) => ({ ...values, ["triggerTime"]: selectedItem.id}));     
        }
    }

    const validateInput = (name: string, value: string) => { 
        if(name === "subject") {            
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Subject is required"}}));
            } 
            else{
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
    }

    const validateStageEmail = (): number => {
        let emailValidation: EmailSubjectValidation = {
            subject: {valid: true}
        }

        if(emailModel.subject === "" || emailModel.subject === undefined) { 
            emailValidation.subject = {valid: false, invalid: true, invalidMessage: "Subject is required"};
        }
        setValidation(emailValidation);       
        const validInputCount = Object.keys(emailValidation).filter(key => emailValidation[key].valid).length;
        return validInputCount; 
    }

    const handlAddEmailClick = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setEmailModel(initailEmailModel);
        setEditEnable(false);
        topRef.current?.scrollIntoView({ behavior: 'smooth' });   
    }

    const handleEditEmailClick = (email: JobApplicationStageEmail, index: number) => {        
        setEmailModel({
            jobApplicationStageEmailId: email.jobApplicationStageEmailId,
            jobId: email.jobId,                        
            applicationStatusId: email.applicationStatusId,
            triggerTime: email.triggerTime,
            subject: email.subject,            
            template: email.template
        })
        validateInput("subject", email.subject);
        setEditEnable(true);
        setEditEmailIndex(index + 1);
        topRef.current?.scrollIntoView({ behavior: 'smooth' });    
    }
    
    const handleDeleteEmailClick = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setOpenDeleteDialog(true);
    }

    const handleDeleteCancel = () => {
        setOpenDeleteDialog(false);    
    }

    const handleDeleteConfirm = () => {
        deleteStageEmail();
        setOpenDeleteDialog(false);
    }

    async function addStageEmail(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = validateStageEmail();
        if(validInputCount == 1) {   
            setIsLoading(true);         
            await postStageEmailTemplate(emailModel, recruiter.recruiterId, recruiter.token)
            .then((response: ResponseModel) => {
                if(response.status) {
                    setStateEmails(prev => [
                        ...prev,
                        {
                            jobApplicationStageEmailId: response.data,
                            jobId: jobId,                                                        
                            applicationStatusId: emailModel.applicationStatusId,
                            triggerTime: emailModel.triggerTime,
                            subject: emailModel.subject,                                                        
                            template: emailModel.template
                        }
                    ]);
                    setIsLoading(false);
                    openAlert("success", "Email template saved successfully.");
                    setEmailModel(initailEmailModel);
                    setValidation(initialValidation);
                    setTimeout(() => {
                        lastMailRef.current?.scrollIntoView({ behavior: "smooth" });
                    }, 0);
                }
                else {
                    openAlert("error", "There was a problem with your submission. Please try again later.");
                }
            })
            .catch((error) => {
                toErrorPage(error);
            })
        }
        else {
            openAlert("error", "Some mandatory fields are invalid.");
        }
    }

    async function updateStageEmail(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = validateStageEmail();
        if(validInputCount == 1) {    
            setIsLoading(true);        
            await updateStageEmailTemplate(emailModel, recruiter.recruiterId, recruiter.token)
            .then((response: ResponseModel) => {
                if(response.status) {
                    setStateEmails(prev => 
                    prev.map((item, index) => 
                        index === editEmailIndex - 1
                            ? {
                                ...item,                                
                                applicationStatusId: emailModel.applicationStatusId,
                                triggerTime: emailModel.triggerTime,
                                subject: emailModel.subject,                                                                
                                template: emailModel.template
                            }
                            : item
                        )
                    );
                    setIsLoading(false);
                    openAlert("success", "Email template Updated successfully.");
                    setEmailModel(initailEmailModel);
                    setValidation(initialValidation);
                    setEditEnable(false);
                } 
                else{
                    openAlert("error", "There was a problem with your submission. Please try again later.");
                }
            })
            .catch((error) => {
                toErrorPage(error);
            })
        }
        else {           
            openAlert("error", "Some mandatory fields are invalid.");
        }
    }

    async function deleteStageEmail(event?: React.FormEvent<HTMLFormElement>) {
        event?.preventDefault();
        setIsLoading(true);
        await deleteStageEmailTemplate(emailModel.jobApplicationStageEmailId, recruiter.recruiterId, recruiter.token)
        .then((success: boolean) => {
            if(success) {
                const updatedList = stageEmails.filter(mail => mail.jobApplicationStageEmailId !== emailModel.jobApplicationStageEmailId);
                setStateEmails(updatedList);
                setIsLoading(false);
                openAlert("success", "Email Deleted Successfully");
                setEmailModel(initailEmailModel);
                setEditEnable(false);
            }
            else {
                openAlert("error", "Question Deleted Unsuccessfully");
            }
        })
        .catch((error) => {
            toErrorPage(error);            
        });
    }

    const handleDiscardClick = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setEmailModel(initailEmailModel);
        setEditEnable(false);
    }

    const handleNext = async (
        event?: React.FormEvent<HTMLFormElement>,
        isSubmit: boolean = false
    ): Promise<boolean> => {
        event?.preventDefault();
        props.onUpdateSummary({
            optional: (stageEmails.length > 0),
            submission: true
        });
        if (isSubmit) {
            try {
                const response: ResponseModel = await submitVacancy(recruiter.recruiterId, recruiter.token, jobId);
                if (response.status) {
                    navigate('/recruiter/vacancy?page=1&status=PENDING', { replace: true });
                    openAlert("success", "Job successfully added to the system");
                    return true;
                } else {
                    openAlert("error", "There was a problem with your submission. Please try again later.");
                    return false;
                }
            } catch (error) {
                toErrorPage(error);
                return false;
            }
        } else {
            props.onTabChange("readyToSubmit");
            setCheck(true);
            return true;
        }
    }

    useImperativeHandle(ref, () => ({
        save: () => handleNext(undefined, !isFinalStage)
    }));

    const handleBack = () => {
        if (props.isCVLess) {
            props.onTabChange("cvLessQuestions");
        } else {
            props.onTabChange("vacancyQuestions");
        }
    };

    const getApplicationStatus = (statusId: number): string | undefined => { 
        if(applicationStatuses) {   
            const applicationStatus = applicationStatuses.find((element) => element.id === statusId);
            return applicationStatus?.value;
        }
        return undefined;
    }

    const getTriggerTimeText = (triggerTime: number): string | undefined => {        
        const item = triggerTimes.find((element) => element.id === triggerTime);
        return item?.value;
    }

    return(
        <>
            {isLoading ? <ReplyMailLoader/> : <div style={styles.mainWrapper}>
                <div style={styles.subTittleWrapper} ref={topRef}>
                    <div style={styles.subTittle}>{"Reply Mail (Optional)"}</div>
                    {!isFinalStage  && <div style={styles.skipButtonWrapper}>
                        {"Skip"}
                        <Switch styles={styles.toggleButton} checked={check} handleClick={handleNext} />
                    </div>}
                </div>
                <div style={styles.emailWrapper}>
                    <div style={styles.emailHeader}>                    
                        {editEnable ? <span>{`${getApplicationStatus(emailModel.applicationStatusId)} (Edit)`}</span> : <span>{"New Mail"}</span>}
                    </div>
                    <div style={styles.groupWrapper}>
                        <div style={styles.gropuInputWrapper}>
                            <Selector
                                label={"Application Stage"}
                                placeHolder={"Select One"}
                                items={applicationStatuses}
                                name={"applicationStatus"}
                                value={getApplicationStatus(emailModel.applicationStatusId)}
                                onSelectItem={handleSelectItems}
                                styles={styles.selector}
                            />
                        </div>
                        <div style={styles.gropuInputWrapper}>
                            <Selector
                                label={"Send After"}
                                placeHolder={"Select Option"}
                                items={triggerTimes}
                                name={"triggerTime"}
                                value={getTriggerTimeText(emailModel.triggerTime)}
                                onSelectItem={handleSelectItems}
                                styles={styles.selector}
                            />
                        </div>
                    </div>
                    <div style={styles.inputWrapper}>
                        <label style={styles.label}>{"Subject *"}</label>
                        <TextInput
                            inputType={"text"}
                            name={"subject"}
                            value={emailModel.subject}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}
                            styles={styles.textInput}
                            validation={validation.subject}     
                        />
                    </div>
                    <div style={styles.emailTitle}>{"Email"}</div>
                    <div>
                        <EmailHtmlEditor  
                            styles={styles.coverLetterEditor}
                            value={emailModel.template} 
                            onChange={(content: string) => setEmailModel((values: any) => ({ ...values, ["template"]: content }))} 
                        />
                    </div>                
                    {editEnable ? <div style={styles.buttonWrapper}>
                        <div style={styles.buttonWrapper.group}>
                            <div style={styles.buttonWrapper.topRow}>
                                <Button style={styles.buttonWrapper.update} onClick={updateStageEmail}>{"UPDATE"}</Button>
                            </div>
                            <div style={styles.buttonWrapper.bottomRow}>
                                <Button style={styles.buttonWrapper.saveDraft} onClick={handleDiscardClick}>{"DISCARD"}</Button>
                                <Button style={styles.buttonWrapper.delete} onClick={handleDeleteEmailClick}>{"DELETE"}</Button>
                            </div>
                        </div>                        
                    </div>
                    : <div style={styles.buttonWrapper}>
                        <Button style={styles.buttonWrapper.submit} onClick={addStageEmail}>{"ADD EMAIL"}</Button>
                        <Button style={styles.buttonWrapper.saveDraft} onClick={handleDiscardClick}>{"DISCARD"}</Button>
                    </div>}
                </div>
                {stageEmails.map((email, index) => {
                    const isLast = index === stageEmails.length - 3;
                    return (
                        <div 
                            key={email.applicationStatusId} 
                            style={styles.emailList}
                            ref={isLast ? lastMailRef : null}
                        >                        
                            {getApplicationStatus(email.applicationStatusId)}
                            <Edit style={styles.editIcon} onClick={() => handleEditEmailClick(email, index)}/>
                        </div>
                    );
                })}   
                <div style={styles.singleButton}>
                    <Button style={styles.singleAddNewQuestionButton} onClick={handlAddEmailClick}>{"ADD NEW EMAIL"}<Plus style={styles.plusIcon}/></Button>
                </div>
                <hr style={styles.line} />
                {!isFinalStage && <div style={styles.buttonsWrapper}>
                    <Button style={styles.buttonsWrapper.back} onClick={handleBack}>{"BACK"}</Button>
                    <Button style={styles.buttonsWrapper.next} onClick={handleNext}>{"NEXT"}</Button>
                </div>}
            </div>}
            <PopUpWindow styles={styles.deleteWarningDialog} open={openDeleteDialog} toggle={()=> setOpenDeleteDialog(!openDeleteDialog)} closeCircle={true}>
                <h1 style={styles.deleteDialogTitle}>{"Are you sure?"}</h1>
                <p style={styles.deleteDialogMessage}>{"This email template will be deleted permanently."}</p>
                <div className="center-content" style={styles.deleteButtons}>
                    <Button style={{...styles.deleteButton, ...styles.deleteButton.red}} onClick={handleDeleteConfirm}>{"YES"}</Button>
                    <Button style={{...styles.deleteButton, ...styles.deleteButton.blue}} onClick={handleDeleteCancel}>{"NO"}</Button> 
                </div>
            </PopUpWindow>
            <Alert />
        </>
    )
})